import React, { useState, useEffect, useRef } from "react"
import { NavLink, Link, useNavigate } from "react-router-dom"
import { useAuth } from "../../../context/AuthContext"
import AuthModal from "../../auth/AuthModal"
import Head from "./Head"
import "./header.css"

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [dropOpen,   setDropOpen]   = useState(false)
  const [modalOpen,  setModalOpen]  = useState(false)
  const [modalMode,  setModalMode]  = useState("login")

  const dropRef  = useRef(null)  // ref for the user-menu wrapper
  const { isUser, isAdmin, user, admin, userLogout, adminLogout } = useAuth()
  const navigate = useNavigate()

  const navClass = ({ isActive }) => isActive ? "nav-active" : ""

  const openModal = (mode) => {
    setModalMode(mode)
    setModalOpen(true)
    setMobileOpen(false)
  }

  const handleLogout = () => {
    if (isUser)  { userLogout();  navigate("/") }
    if (isAdmin) { adminLogout(); navigate("/") }
    setDropOpen(false)
  }

  // Close dropdown when clicking anywhere OUTSIDE the menu wrapper
  useEffect(() => {
    if (!dropOpen) return
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [dropOpen])

  // Close dropdown on route change (page navigation)
  useEffect(() => {
    setDropOpen(false)
  }, [navigate])

  const displayName = isUser ? user?.name : isAdmin ? admin?.name : null
  const initials    = displayName?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "U"

  return (
    <>
      <Head />
      <header>
        <nav className="flexSB">

          {/* ── Nav links ── */}
          <ul
            className={mobileOpen ? "mobile-nav" : "flexSB"}
            onClick={() => setMobileOpen(false)}
          >
            <li><NavLink to="/"        end className={navClass}>Home</NavLink></li>
            <li><NavLink to="/courses"     className={navClass}>All Courses</NavLink></li>
            <li><NavLink to="/live"        className={navClass}>Live Sessions</NavLink></li>
            <li><NavLink to="/about"       className={navClass}>About</NavLink></li>
            <li><NavLink to="/team"        className={navClass}>Team</NavLink></li>
            <li><NavLink to="/pricing"     className={navClass}>FAQ</NavLink></li>
            <li><NavLink to="/journal"     className={navClass}>Journal</NavLink></li>
            <li><NavLink to="/contact"     className={navClass}>Contact</NavLink></li>

            {/* Mobile-only auth actions */}
            {!isUser && !isAdmin && (
              <>
                <li className="mobile-only">
                  <button className="mob-auth-btn" onClick={() => openModal("login")}>
                    <i className="fa fa-sign-in-alt"></i> Sign In
                  </button>
                </li>
                <li className="mobile-only">
                  <button className="mob-auth-btn mob-register" onClick={() => openModal("register")}>
                    <i className="fa fa-user-plus"></i> Register Free
                  </button>
                </li>
              </>
            )}
            {isUser && (
              <li className="mobile-only">
                <NavLink to="/dashboard" className={navClass} onClick={() => setMobileOpen(false)}>
                  <i className="fa fa-th-large"></i> My Dashboard
                </NavLink>
              </li>
            )}
            {isAdmin && (
              <li className="mobile-only">
                <NavLink to="/admin/dashboard" className={navClass} onClick={() => setMobileOpen(false)}>
                  <i className="fa fa-shield-alt"></i> Admin Panel
                </NavLink>
              </li>
            )}
          </ul>

          {/* ── Desktop right side ── */}
          <div className="header-right">
            {!isUser && !isAdmin ? (
              <div className="header-auth-btns">
                <button className="btn-login"    onClick={() => openModal("login")}>Login</button>
                <button className="btn-register" onClick={() => openModal("register")}>Register</button>
              </div>
            ) : (
              /* Click-only dropdown — NO onMouseEnter / onMouseLeave */
              <div className="header-user-menu" ref={dropRef}>
                <button
                  className="header-user-btn"
                  onClick={() => setDropOpen(v => !v)}
                  aria-expanded={dropOpen}
                  aria-haspopup="true"
                >
                  <div className="header-avatar">{initials}</div>
                  <span>{displayName?.split(" ")[0]}</span>
                  <i className={`fa fa-chevron-${dropOpen ? "up" : "down"}`}></i>
                </button>

                {dropOpen && (
                  <div className="header-dropdown">
                    <div className="dropdown-user-info">
                      <div className="dropdown-avatar">{initials}</div>
                      <div>
                        <div className="dropdown-name">{displayName}</div>
                        <div className="dropdown-role">
                          {isAdmin ? `Admin · ${admin?.role}` : "Student"}
                        </div>
                      </div>
                    </div>

                    <div className="dropdown-divider"></div>

                    {isUser && (
                      <Link
                        to="/dashboard"
                        className="dropdown-item"
                        onClick={() => setDropOpen(false)}
                      >
                        <i className="fa fa-th-large"></i> My Dashboard
                      </Link>
                    )}
                    {isAdmin && (
                      <Link
                        to="/admin/dashboard"
                        className="dropdown-item"
                        onClick={() => setDropOpen(false)}
                      >
                        <i className="fa fa-shield-alt"></i> Admin Panel
                      </Link>
                    )}

                    <div className="dropdown-divider"></div>

                    <button className="dropdown-item dropdown-logout" onClick={handleLogout}>
                      <i className="fa fa-sign-out-alt"></i> Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Hamburger */}
          <button className="toggle" onClick={() => setMobileOpen(v => !v)}>
            {mobileOpen ? <i className="fa fa-times" /> : <i className="fa fa-bars" />}
          </button>

        </nav>
      </header>

      {/* Auth modal */}
      <AuthModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
      />
    </>
  )
}

export default Header
